import React, { useState, useRef, useEffect, useCallback } from 'react';
import { AutoComplete, Input, Tag, Empty, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

/**
 * 全局搜索框（2026-10-26 新建）
 *
 * 起因（nginx 日志实测）：`/vehicles/:id` 225 次 = 全站最高频请求，
 *   说明「找一台车 → 进详情」是每天重复最多的动作，而它现在要先过 18 个菜单。
 *
 * 能力：一个框搜 车牌 / 车辆编号 / VIN / 司机姓名 / 身份证 / 手机号 / 司机编号 / 车主
 *       结果分「车辆」「司机」两组，点任意一条直接落详情页。
 * 快捷键：Ctrl/⌘ + K 聚焦（和浏览器/编辑器一致的习惯）
 */
export default function GlobalSearch() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const seqRef = useRef(0);
  const inputRef = useRef(null);

  // ⌘K / Ctrl+K 聚焦
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const doSearch = useCallback(async (kw) => {
    const q = String(kw || '').trim();
    if (!q) { setOptions([]); setLoading(false); return; }
    const mySeq = ++seqRef.current;
    setLoading(true);
    try {
      const res = await api.get('/search', { params: { q, limit: 8 } });
      // 防止「后发先至」：只认最后一次请求的结果
      if (mySeq !== seqRef.current) return;
      const d = res.data || {};
      const groups = [];

      if ((d.vehicles || []).length) {
        groups.push({
          label: <span style={{ fontSize: 12, color: '#999' }}>车辆（{d.vehicles.length}）</span>,
          options: d.vehicles.map((v) => ({
            value: `v:${v.id}:${v.plate_number || ''}`,
            label: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
                <Tag color="blue" style={{ margin: 0 }}>{v.plate_number || '无车牌'}</Tag>
                <span style={{ fontSize: 13 }}>{v.driver_name || '—'}</span>
                {v.driver_code ? <span style={{ fontSize: 12, color: '#999' }}>编号 {v.driver_code}</span> : null}
                {v.is_active === 0 ? <Tag color="red" style={{ margin: 0 }}>已封存</Tag> : null}
                <span style={{ fontSize: 12, color: '#bbb', marginLeft: 'auto' }}>{v.city || ''}</span>
              </div>
            ),
          })),
        });
      }

      if ((d.drivers || []).length) {
        groups.push({
          label: <span style={{ fontSize: 12, color: '#999' }}>司机（{d.drivers.length}）</span>,
          options: d.drivers.map((x) => ({
            value: `d:${x.id}:${x.vehicle_id || ''}`,
            label: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
                <Tag color="green" style={{ margin: 0 }}>{x.name || '无姓名'}</Tag>
                <span style={{ fontSize: 12, color: '#666' }}>{x.driver_code || ''}</span>
                <span style={{ fontSize: 12, color: '#999' }}>{x.phone || ''}</span>
                <span style={{ fontSize: 12, color: '#bbb', marginLeft: 'auto' }}>
                  {x.plate_number || '未绑定车辆'}
                </span>
              </div>
            ),
          })),
        });
      }

      if (!groups.length) {
        groups.push({
          label: '',
          options: [{
            value: '__none__',
            label: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="没找到" style={{ margin: '8px 0' }} />,
            disabled: true,
          }],
        });
      }
      setOptions(groups);
    } catch (e) {
      if (mySeq === seqRef.current) setOptions([]);
    } finally {
      if (mySeq === seqRef.current) setLoading(false);
    }
  }, []);

  // 输入防抖 300ms
  const onSearch = (kw) => {
    setValue(kw);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!String(kw || '').trim()) { setOptions([]); setLoading(false); return; }
    setLoading(true);
    timerRef.current = setTimeout(() => doSearch(kw), 300);
  };

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const onSelect = (val) => {
    if (!val || val === '__none__') return;
    const parts = String(val).split(':');
    const kind = parts[0];
    if (kind === 'v') {
      navigate(`/vehicles/${parts[1]}`);
    } else if (kind === 'd') {
      // 司机有绑定车 → 直接进车详情；没有 → 进司机列表
      if (parts[2]) navigate(`/vehicles/${parts[2]}`);
      else navigate(`/drivers?search=${encodeURIComponent(value)}`);
    }
    setValue('');
    setOptions([]);
    inputRef.current?.blur();
  };

  return (
    <AutoComplete
      value={value}
      options={options}
      onSearch={onSearch}
      onSelect={onSelect}
      popupMatchSelectWidth={420}
      style={{ width: 300 }}
      notFoundContent={loading ? <Spin size="small" /> : null}
    >
      <Input
        ref={inputRef}
        allowClear
        prefix={<SearchOutlined style={{ color: '#bbb' }} />}
        placeholder="搜车牌 / 姓名 / 手机号 / 编号"
        suffix={
          <span style={{ fontSize: 11, color: '#ccc', border: '1px solid #eee', borderRadius: 4, padding: '0 4px' }}>
            ⌘K
          </span>
        }
        onPressEnter={() => { if (String(value).trim()) doSearch(value); }}
      />
    </AutoComplete>
  );
}
